import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  inject,
} from '@angular/core';
import { FolderDto, GalleryDto } from '@nusign/picflow-backend-types';

import { fuzzySearch } from '../../../lib/fuzzy-search';
import { sleep } from '../../../lib/sleep';
import { FolderGalleryFilterService } from '../../../services/folder-gallery-filter.service';
import { GalleryDisplayPreferencesService } from '../../../services/gallery-display-preferences.service';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { sortGalleriesAndFolders } from '../../../shared/pipes/galleries-sort-by.pipe';
import { GalleryTeaserUrlPipe } from '../../../shared/pipes/gallery-teaser-url.pipe';

type GalleryTree = { gallery: GalleryDto; children: GalleryDto[]; active: boolean; disabled: boolean }[];

@Component({
  selector: 'app-finder-content',
  templateUrl: './finder-content.component.html',
  styleUrls: ['./finder-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SvgComponent, TooltipDirective, ButtonDirective, LoadingIndicatorComponent, GalleryTeaserUrlPipe],
})
export class FinderContentComponent implements OnChanges {
  @Input() allGalleries: GalleryDto[];
  @Input() allFolders: FolderDto[];
  @Input() currentFolder: FolderDto;
  @Input() currentGallery: GalleryDto;
  @Input() searchMode: boolean;
  @Input() searchTerm: string;
  @Input() filterOut: (FolderDto | GalleryDto)[] = [];
  @Input() disableList: (FolderDto | GalleryDto)[] = [];
  @Input() hideSubGalleries = false;
  @Input() folderMode = false;
  @Input() blockGallerySelection = false;
  @Input() cta?: string;
  @Input() simpleMode = false;
  @Input() loading?: string;
  @Output() onSelectFolder = new EventEmitter<FolderDto>();
  @Output() onSelectGallery = new EventEmitter<GalleryDto>();
  @Output() onResultsUpdated = new EventEmitter<(FolderDto | GalleryDto)[]>();
  galleryTree: GalleryTree = [];
  folders: FolderDto[] = [];
  autoScrolled = false;

  folderGalleryCount: { [folderId: string]: number } = {};

  private galleryDisplayPreferences = inject(GalleryDisplayPreferencesService);
  private el = inject(ElementRef<HTMLElement>);
  private check = inject(ChangeDetectorRef);
  private filterService = inject(FolderGalleryFilterService);

  async ngOnChanges() {
    if (!this.allGalleries || !this.allFolders) return;
    this.autoScroll();
    this.calcView();
    this.calcActiveGalleries();
    this.allFolders.forEach(folder => {
      const folderGalleries = this.allGalleries.filter(g => g.folder === folder.id);
      this.folderGalleryCount[folder.id] = folderGalleries.length;
    });
    this.check.detectChanges();
  }

  private async autoScroll() {
    if (!this.currentGallery) return;
    if (this.autoScrolled) return;
    await sleep(0);
    const el = this.el.nativeElement.querySelector('#' + this.currentGallery?.id);
    if (el) el.scrollIntoView();
    this.autoScrolled = true;
    this.check.detectChanges();
  }

  selectFolder(folder: FolderDto, event?: Event) {
    if (event) event.stopPropagation();
    this.onSelectFolder.emit(folder);
    this.check.detectChanges();
  }

  selectGallery(gallery: GalleryDto, event?: Event) {
    if (event) event.stopPropagation();
    if (this.blockGallerySelection) return;
    this.onSelectGallery.emit(gallery);
    this.check.detectChanges();
    return false;
  }

  calcView() {
    const { galleries, folders } = this.getGalleriesAndFolders();
    if (!this.currentFolder) {
      this.folders = folders.filter(f => f.parent_folder === null);
      this.galleryTree = this.constructGalleryTree(galleries.filter(g => g.folder === null || g.section));
    } else if (this.searchMode) {
      this.folders = folders;
      this.galleryTree = this.constructGalleryTree(galleries.filter(g => g.folder === null || g.section));
    } else {
      this.folders = folders.filter(f => f.parent_folder === this.currentFolder.id);
      this.galleryTree = this.constructGalleryTree(
        galleries.filter(g => g.folder === this.currentFolder.id || g.section)
      );
    }
    this.calcActiveGalleries();
    this.updateResults();
    this.check.detectChanges();
  }

  async updateResults() {
    await sleep(0);
    this.onResultsUpdated.emit([...this.folders, ...this.galleryTree.map(g => g.gallery)]);
  }

  private getGalleriesAndFolders() {
    const galleries = this.allGalleries.filter(g => !this.filterOut.find(o => o?.id === g.id));
    const folders = this.allFolders.filter(f => !this.filterOut.find(o => o?.id === f.id));
    if (!this.searchMode) {
      return {
        galleries,
        folders,
      };
    }

    const { galleries: filteredGalleries, folders: filteredFolders } = this.filterService.filterFoldersAndGalleries(
      folders,
      galleries,
      false
    );

    return {
      galleries: fuzzySearch(filteredGalleries, ['title', 'name', 'description'], this.searchTerm),
      folders: fuzzySearch(filteredFolders, ['title'], this.searchTerm),
    };
  }

  constructGalleryTree(galleries: GalleryDto[]): GalleryTree {
    const prefs = this.galleryDisplayPreferences.displayPreferences$.value;
    return sortGalleriesAndFolders(galleries, prefs.appearance === 'list', prefs.sorting, prefs.pinFavourites)
      .filter(g => !g.section)
      .map(gallery => {
        const disabled = this.disableList.some(g => gallery.id === g.id);
        if (this.hideSubGalleries) return { gallery, children: [], active: false, disabled };
        const children = galleries.filter(g => g['parent_gallery_id'] === gallery.id);
        return { gallery, children, active: false, disabled };
      });
  }

  calcActiveGalleries() {
    this.galleryTree.forEach(item => {
      item.active = false;
      if (!this.currentGallery) return;
      if (item.gallery.id === this.currentGallery.id) item.active = true;
      if (item.children.some(c => c.id === this.currentGallery.id)) item.active = true;
    });
  }
}
